import { useState, useRef, useEffect } from 'react';
import { STATUS_CONFIG, ALL_STATUSES, CTRL_KEY_STATUS_MAP } from '@/utils/taskStatusConfig';
import type { TaskStatus } from '@/utils/taskStatusConfig';

interface StatusDropdownProps {
    currentStatus: TaskStatus;
    onStatusChange: (status: TaskStatus) => void;
    disabled?: boolean;
    isLoading?: boolean;
}

export const StatusDropdown = ({
    currentStatus,
    onStatusChange,
    disabled = false,
    isLoading = false,
}: StatusDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const config = STATUS_CONFIG[currentStatus];
    const { Icon } = config;

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsOpen(false);
                return;
            }
            if (e.ctrlKey && CTRL_KEY_STATUS_MAP[e.key]) {
                e.preventDefault();
                const mapped = CTRL_KEY_STATUS_MAP[e.key]!;
                if (mapped !== currentStatus) onStatusChange(mapped);
                setIsOpen(false);
            }
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isOpen, onStatusChange, currentStatus]);

    const handleSelect = (status: TaskStatus) => {
        if (status !== currentStatus) onStatusChange(status);
        setIsOpen(false);
    };

    const isInteractive = !disabled && !isLoading;

    return (
        <div ref={containerRef} className="relative inline-block">
            <button
                type="button"
                onClick={() => isInteractive && setIsOpen((prev) => !prev)}
                disabled={!isInteractive}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label={`Task status: ${config.label}. Click to change.`}
                className={`
                    inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border
                    transition-colors duration-200 whitespace-nowrap
                    ${config.bgClass} ${config.textClass} ${config.borderClass}
                    ${isInteractive ? 'cursor-pointer hover:opacity-80' : 'opacity-60 cursor-not-allowed'}
                `}
            >
                {isLoading ? (
                    <svg
                        className="w-3.5 h-3.5 animate-spin flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                    >
                        <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                        />
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                    </svg>
                ) : (
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
                )}
                {config.label}
                {!isLoading && (
                    <svg
                        className="w-3 h-3 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                )}
            </button>

            {isOpen && (
                <div
                    role="listbox"
                    aria-label="Select task status"
                    className="absolute z-50 mt-1 min-w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1 left-0"
                >
                    <div className="px-3 py-1 text-xs text-gray-600 border-b border-gray-100 mb-1">
                        Ctrl+1/2/3/B for shortcuts
                    </div>
                    {ALL_STATUSES.map((status) => {
                        const opt = STATUS_CONFIG[status];
                        const isSelected = status === currentStatus;
                        return (
                            <button
                                key={status}
                                role="option"
                                aria-selected={isSelected}
                                onClick={() => handleSelect(status)}
                                className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50 transition-colors duration-150"
                            >
                                <span
                                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${opt.bgClass} ${opt.textClass} ${opt.borderClass}`}
                                >
                                    <opt.Icon className="w-3 h-3" aria-hidden="true" />
                                    {opt.label}
                                </span>
                                {isSelected && (
                                    <svg
                                        className="w-3 h-3 ml-auto text-gray-400 flex-shrink-0"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                        aria-hidden="true"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

StatusDropdown.displayName = 'StatusDropdown';
